import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Phone, Mail } from 'lucide-react';
import AnimatedPage from '../components/AnimatedPage';

const Fullscript = () => {
  return (
    <AnimatedPage className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-stone-50 via-stone-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              Fullscript
            </h1>
            <div className="h-1 w-24 bg-gradient-to-r from-amber-500 via-amber-500 to-orange-500 mx-auto mb-6"></div>
            <p className="text-xl text-stone-700 leading-relaxed max-w-3xl mx-auto">
              Get the best supplements and more on Fullscript
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Benefits Text - No Box */}
          <div className="mb-12 text-center">
            <p className="text-lg text-stone-700 leading-relaxed max-w-4xl mx-auto">
              Your supplement plans will be sent directly to your device, and your orders can be delivered directly to your door. 
              Access healthcare's best wellness products with personalized refill reminders, Autoship option, and fast shipping.
            </p>
          </div>

          {/* How to Get Started */}
          <Card className="glass-card-strong shadow-strong card-premium mb-12">
            <CardContent className="p-12">
              <h2 className="text-3xl font-bold text-stone-900 mb-8 text-center">
                Here's how to get started:
              </h2>

              <div className="space-y-6 max-w-4xl mx-auto">
                {/* Step 1 */}
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-stone-900 mb-2">Check for an email from Fullscript</h3>
                    <p className="text-stone-700 mb-2">
                      Or follow this link: <a href="https://us.fullscript.com/welcome/drawilliams" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">https://us.fullscript.com/welcome/drawilliams</a>
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-stone-900 mb-2">Activate your account</h3>
                    <p className="text-stone-700">
                      Enter your email address and create a password to activate your account.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-stone-900 mb-2">Browse and add to cart</h3>
                    <p className="text-stone-700">
                      Once signed in, browse the catalog and add products to your cart.
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-stone-900 mb-2">Check out</h3>
                    <p className="text-stone-700">
                      Select the shopping cart in the top right corner and follow the steps to check out. 
                      Provide your shipping address and credit card information to place your order.
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center mt-10">
                <a href="https://us.fullscript.com/welcome/drawilliams" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold px-12 py-6 text-lg shadow-xl">
                    Get Started with Fullscript
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Customer Support */}
          <Card className="glass-card-strong shadow-strong card-premium">
            <CardContent className="p-12">
              <h2 className="text-3xl font-bold text-stone-900 mb-8 text-center">
                Need assistance?
              </h2>
              <p className="text-lg text-stone-700 text-center mb-8">
                Contact Fullscript's Customer Support team
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                <Card className="border-2 border-stone-200">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="bg-gradient-to-br from-stone-50 to-stone-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900 mb-1">Phone</h3>
                        <a href="tel:18668073828" className="text-stone-600 hover:text-orange-600 font-medium">
                          1-866-807-3828
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-stone-200">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="bg-gradient-to-br from-stone-50 to-stone-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900 mb-1">Email</h3>
                        <a href="mailto:support@fullscript.com" className="text-stone-600 hover:text-orange-600 font-medium break-all">
                          support@fullscript.com
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-8 text-center">
                <h4 className="font-semibold text-stone-900 mb-3">Support Hours:</h4>
                <p className="text-stone-700">Monday - Thursday: 9:00 AM - 9:00 PM EST</p>
                <p className="text-stone-700">Friday - Sunday: 9:00 AM - 5:00 PM EST</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </AnimatedPage>
  );
};

export default Fullscript;
